import { useCallback, useEffect, useRef, useState } from 'react';
import './Board.scss';
import '../recruit/common/RecruitList.scss';
import MainURL from '../../MainURL';
import ImageURL from '../../ImageURL';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import DateFormmating from '../../components/DateFormmating';
import Pagination from '../../components/Pagination';
import Loading from '../../components/Loading';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import { useAtomValue } from 'jotai';
import { loginStateAtom } from '../../AtomStore';
import '../ForListPage.scss';
import type { CommunityBoardConfig, CommunityPost } from './BoardTypes';
import {
  isBoardNoticePost,
  parseBoardListPayload,
  parsePostImages,
  stripHtmlToPlain,
} from './boardListUtils';
import type { BoardListLocationState, BoardReturnState } from './boardNavigation';
import {
  boardReturnShouldRestoreScroll,
  clearBoardListRestore,
  resolveInitialBoardReturn,
  restoreScroll,
  saveBoardListRestore,
} from './boardNavigation';
import BoardFeedListItem from './BoardFeedListItem';
import BoardImageListItem from './BoardImageListItem';

type Props = {
  config: CommunityBoardConfig;
};

const getListRoute = (config: CommunityBoardConfig) => config.listRoute ?? `${config.routePrefix}getposts`;
const getSearchRoute = (config: CommunityBoardConfig) =>
  config.searchRoute ?? `${config.routePrefix}getpostssearch`;
const getViewsRoute = (config: CommunityBoardConfig) => config.viewsRoute ?? `${config.routePrefix}postsviews`;

/** 모바일 화면 여부 — Board.scss의 모바일 브레이크포인트(600px)와 동일 */
const MOBILE_BREAKPOINT = 600;

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

export default function BoardList({ config }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRestoreRef = useRef<BoardReturnState | null | undefined>(undefined);
  if (initialRestoreRef.current === undefined) {
    initialRestoreRef.current = resolveInitialBoardReturn(
      location.state as BoardListLocationState | null,
      config.listPath,
    );
  }
  const initialRestore = initialRestoreRef.current;

  const isLogin = useAtomValue(loginStateAtom);
  const hasRegion = Boolean(config.regionOptions?.length);
  const isMobile = useIsMobileViewport();

  const [currentPage, setCurrentPage] = useState(initialRestore?.currentPage ?? 1);
  const [noticeList, setNoticeList] = useState<CommunityPost[]>([]);
  const [list, setList] = useState<CommunityPost[]>([]);
  const [listAllLength, setListAllLength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** null = 해당 행에서 「전체」 선택 */
  const [activeCategory, setActiveCategory] = useState<string | null>(
    initialRestore?.activeCategory ?? null,
  );
  const [activeRegion, setActiveRegion] = useState<string | null>(initialRestore?.activeRegion ?? null);
  const pendingScrollYRef = useRef<number | null>(
    initialRestore && boardReturnShouldRestoreScroll(initialRestore)
      ? (initialRestore.scrollY ?? 0)
      : null,
  );
  const hasInitializedRef = useRef(false);
  const endReachedLockRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const itemsPerPage = 12;
  const totalPages = Math.ceil(listAllLength / itemsPerPage);
  const listRoute = getListRoute(config);
  const searchRoute = getSearchRoute(config);
  const viewsRoute = getViewsRoute(config);
  const isFiltering = activeCategory != null || activeRegion != null;
  const hasMore = isMobile && !isLoading && !loadingMore && list.length > 0 && list.length < listAllLength;

  const fetchDatas = useCallback(
    async (
      page: number,
      categories: string[],
      regions: string[],
    ) => {
      const isFirstPage = page === 1;
      const appendMode = isMobile && !isFirstPage;
      if (appendMode) setLoadingMore(true);
      else setIsLoading(true);

      const hasFilter = categories.length > 0 || regions.length > 0;
      try {
        const res = hasFilter
          ? await axios.post(`${MainURL}/${config.apiBase}/${searchRoute}`, {
              word: '',
              categories,
              regions,
              page,
            })
          : await axios.get(`${MainURL}/${config.apiBase}/${listRoute}/${page}`);

        if (res.data) {
          const { notices, regular } = parseBoardListPayload(
            res.data.resultData as CommunityPost[],
            res.data.noticePosts as CommunityPost[],
          );
          if (!appendMode) {
            setNoticeList(notices);
            setList(regular);
            setListAllLength(res.data.totalCount || 0);
          } else if (regular.length === 0) {
            setList((prev) => {
              setListAllLength(prev.length);
              return prev;
            });
          } else {
            setList((prev) => [...prev, ...regular]);
            setListAllLength(res.data.totalCount || 0);
          }
        } else if (!appendMode) {
          setNoticeList([]);
          setList([]);
          setListAllLength(0);
        } else {
          setList((prev) => {
            setListAllLength(prev.length);
            return prev;
          });
        }
      } catch (error) {
        console.error(error);
        if (!appendMode) {
          setNoticeList([]);
          setList([]);
          setListAllLength(0);
        } else {
          setList((prev) => {
            setListAllLength(prev.length);
            return prev;
          });
        }
      } finally {
        setIsLoading(false);
        setLoadingMore(false);
        endReachedLockRef.current = false;
        if (pendingScrollYRef.current !== null) {
          restoreScroll(pendingScrollYRef.current);
          pendingScrollYRef.current = null;
          clearBoardListRestore(config.listPath);
          navigate(config.listPath, { replace: true, state: {} });
        }
      }
    },
    [config.apiBase, config.listPath, listRoute, searchRoute, navigate, isMobile],
  );

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (initialRestore && pendingScrollYRef.current === null) {
        clearBoardListRestore(config.listPath);
        navigate(config.listPath, { replace: true, state: {} });
      }
    }

    const categories = activeCategory ? [activeCategory] : [];
    const regions = activeRegion ? [activeRegion] : [];
    void fetchDatas(currentPage, categories, regions);
  }, [currentPage, activeCategory, activeRegion, fetchDatas]);

  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !endReachedLockRef.current) {
          endReachedLockRef.current = true;
          setCurrentPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, hasMore]);

  const openPostDetails = (post: CommunityPost) => {
    axios
      .post(`${MainURL}/${config.apiBase}/${viewsRoute}`, {
        postId: post.id,
        sort: config.sort,
      })
      .then(() => {
        const boardReturn: BoardReturnState = {
          currentPage,
          scrollY: window.scrollY,
          activeCategory,
          activeRegion,
        };
        saveBoardListRestore(config.listPath, boardReturn);
        window.scrollTo(0, 0);
        navigate(config.detailPath, {
          state: { data: post, sort: config.sort, menuNum: 1, boardReturn },
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const openPostPage = () => {
    if (!isLogin) {
      alert('권한이 없습니다. 로그인이 필요합니다.');
    } else {
      navigate(config.postPath);
    }
  };

  const handleCategorySelect = (item: string) => {
    clearBoardListRestore(config.listPath);
    if (item === '전체') {
      setActiveCategory(null);
    } else {
      setActiveCategory((prev) => (prev === item ? null : item));
    }
    setCurrentPage(1);
  };

  const displayList = [...noticeList, ...list];

  const handleRegionSelect = (item: string) => {
    clearBoardListRestore(config.listPath);
    if (item === '전체') {
      setActiveRegion(null);
    } else {
      setActiveRegion((prev) => (prev === item ? null : item));
    }
    setCurrentPage(1);
  };

  const renderFilterChip = (
    key: string,
    label: string,
    selected: boolean,
    onActivate: () => void,
    isAll = false,
  ) => (
    <button
      key={key}
      type="button"
      className={[
        'board-list__filter-chip',
        isAll ? 'board-list__filter-chip--all' : '',
        selected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onActivate}
      aria-pressed={selected}
    >
      {label}
    </button>
  );

  return (
    <div className="Board recruit">
      <div className="inner">
        <div className="subpage__main">
          <div className="subpage__main__title">
            <h3>{config.boardTitle}</h3>
            <div className="postBtn" onClick={openPostPage}>
              <p>글쓰기</p>
            </div>
          </div>

          <div className="board-list__filters">
            <div className="board-list__filter-row">
              <span className="board-list__filter-label">구분:</span>
              <div className="board-list__filter-chips">
                {renderFilterChip(
                  'category-all',
                  '전체',
                  activeCategory === null,
                  () => handleCategorySelect('전체'),
                  true,
                )}
                {config.categoryOptions.map((item) =>
                  renderFilterChip(
                    `category-${item}`,
                    item,
                    activeCategory === item,
                    () => handleCategorySelect(item),
                  ),
                )}
              </div>
            </div>
            {hasRegion && (
              <div className="board-list__filter-row">
                <span className="board-list__filter-label">지역:</span>
                <div className="board-list__filter-chips">
                  {renderFilterChip(
                    'region-all',
                    '전체',
                    activeRegion === null,
                    () => handleRegionSelect('전체'),
                    true,
                  )}
                  {(config.regionOptions ?? []).map((item) =>
                    renderFilterChip(
                      `region-${item}`,
                      item,
                      activeRegion === item,
                      () => handleRegionSelect(item),
                    ),
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="subpage__main__content">
            {isLoading ? (
              <div className="list-loading">
                <Loading />
              </div>
            ) : (
              <div className="main__content">
                <div className="community-board__wrap">
                  <div
                    className={
                      config.listVariant === 'feed'
                        ? 'community-board__list'
                        : 'community-board__list community-board__list--grid'
                    }
                  >
                    {displayList.length > 0 ? (
                      displayList.map((item) => {
                        const isNotice = isBoardNoticePost(item);
                        const dateLabel = DateFormmating(item.date);

                        if (config.listVariant === 'feed') {
                          return (
                            <BoardFeedListItem
                              key={item.id}
                              config={config}
                              post={item}
                              contentText={stripHtmlToPlain(item.content)}
                              dateLabel={dateLabel}
                              onOpenDetail={() => openPostDetails(item)}
                            />
                          );
                        }

                        const firstImage = parsePostImages(item.images)[0];
                        return (
                          <BoardImageListItem
                            key={item.id}
                            post={item}
                            imageUri={
                              firstImage
                                ? `${ImageURL}/images/postimage/${config.imagePath}/${firstImage}`
                                : undefined
                            }
                            dateLabel={dateLabel}
                            isNotice={isNotice}
                            onOpenDetail={() => openPostDetails(item)}
                          />
                        );
                      })
                    ) : (
                      <div className="community-board__empty">
                        {isFiltering ? '검색 결과가 없습니다.' : '등록된 글이 없습니다.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isMobile ? (
              <>
                {loadingMore && (
                  <div className="board-list__loading-more">
                    <Loading />
                  </div>
                )}
                <div ref={sentinelRef} className="board-list__sentinel" />
              </>
            ) : (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  clearBoardListRestore(config.listPath);
                  setCurrentPage(page);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </div>
  );
}
